/* eslint-disable */
import Icon from 'components/icon';
import email from 'components/icon/pngs/send_mail_provider_icon.png';
import videoLinkSMS from 'components/icon/pngs/sms-52.png';
import videoLinkChat from 'components/icon/pngs/video-link-white-96.png';
import Toast from 'components/toast';
import { NotificationMethod } from 'node-api/appointments/appointments.types';
import { appointmentsClient } from 'node-api/appointments/client';
import React, { useContext, useEffect, useState } from 'react';
import { Mention, MentionsInput, OnChangeHandlerFunc } from 'react-mentions';
import ReactTooltip from 'react-tooltip';
import { TrackableElement } from 'services/mixpanel/mixpanel-service';
import { MixpanelWrapperInstance } from 'services/mixpanel/mixpanel-wrapper';
import { ClickElementTypes, EventHandlerNames } from 'services/mixpanel/mixpanel.types';
import { EventNames } from 'services/mixpanel/provider-app-mixpanel-events';
import { ClickableElement } from 'services/mixpanel/shared-mixpanel-service';
import { ConfirmationContext } from 'state/contexts/confirmation';
import { PatientContext } from 'state/contexts/patient';
import { ProviderContext } from 'state/contexts/provider';
import { UserContext } from 'state/contexts/user';
import { ActionTypes as ConfirmationActionTypes } from 'state/reducers/confirmation';
import { Member, ActionTypes as MemberActionTypes } from 'state/reducers/patient';
import { Provider } from 'state/reducers/provider';
import {
  getCurrentProviderState,
  getOriginalUserFullName,
  isCurrentProviderHidden,
} from 'utils/helpers';

import { actionStyle, inputStyle, mentionStyle } from './mention-style';
import styles from './MessageInput.module.css';
import { IconContainer, InputMessageBox, SendButton } from './style';

type ProviderSummary = {
  id: number;
  display: string;
};

type Action = {
  id: string;
  display: string;
};

type Props = {
  type: string;
  patient: Member;
  onClick: (message: string) => void;
  autoFocus?: boolean;
};

const MessageInput: React.FC<Props> = ({ type, patient, onClick, autoFocus }) => {
  const { userState } = useContext(UserContext);
  const { providerState } = useContext(ProviderContext);
  const [currentProviderState] = useState(getCurrentProviderState(userState, providerState));
  const isCurProviderHidden = isCurrentProviderHidden(currentProviderState);
  const { confirmationDispatch } = useContext(ConfirmationContext);
  const { patientState, patientDispatch } = useContext(PatientContext);

  // TODO: Should be moved into a new Toolbar component (exclusively for MessageInput component)
  const [meetingConnectionLinkToolbarStatuses, setMeetingConnectionLinkToolbarStatuses] = useState<{
    chat: boolean;
    email: boolean;
    sms: boolean;
  }>({
    chat: true,
    email: true,
    sms: true,
  });

  const [value, setValue] = useState<string>('');
  const [providers, setProviders] = useState<ProviderSummary[]>([]);

  const handleOnChange: OnChangeHandlerFunc = (e) => {
    setValue(e.target.value);
    if (patient.chat?.message) {
      patientDispatch({ type: MemberActionTypes.CLEAR_MESSAGE, payload: { memberId: patient.id } });
    }
  };

  const handleOnClick = (): void => {
    onClick(value);
    setValue('');
  };

  // Add keyboard listener for command + enter (on mac) or control + enter (on windows)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleOnClick();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleOnClick]);

  const sendMeetingConnectionLinkViaEmail = async (): Promise<void> => {
    setMeetingConnectionLinkToolbarStatuses((prevState) => ({ ...prevState, email: false }));

    const response = await appointmentsClient.sendMeetingConnectionLink(
      {
        memberUuid: patient.uuid,
        providerUuid: userState.uuid!,
      },
      [NotificationMethod.Email]
    );

    const emailSent = response ? !!response[patient.uuid].email : false;

    setMeetingConnectionLinkToolbarStatuses((prevState) => ({ ...prevState, email: true }));
    if (emailSent) {
      MixpanelWrapperInstance.track(EventNames.MemberProfileEmailReminder, {
        targetLabel: 'Email video link reminder',
        targetType: ClickableElement.BUTTON,
        memberUuid: patient.uuid,
        memberName: `${patient.fullname}`,
        providerUuid: userState.uuid,
        source: 'email-video-link-reminder',
        videoVersion: 'v2',
      });

      Toast.show('success', 'The connection link was sent via email');
      return;
    }

    Toast.show('error', "Couldn't send the connection link via email");
  };

  const appendMeetingConnectionLinkToChat = async (): Promise<void> => {
    setMeetingConnectionLinkToolbarStatuses((prevState) => ({ ...prevState, chat: false }));

    let link: string = '';
    try {
      const meetingData = await appointmentsClient.getVideoMeetingData(
        patient.uuid,
        userState.uuid!
      );

      const meetingParticipant = meetingData.participants.find(
        ({ userUuid }) => userUuid === patient.uuid
      );

      if (!meetingParticipant) {
        Toast.show('error', 'Could not get participant data');
      } else {
        link = meetingParticipant.connectionUrl;
      }
    } catch (err) {
      /** We want to continue the execution so we can alert providers there was an error at the end. */
    }

    setMeetingConnectionLinkToolbarStatuses((prevState) => ({ ...prevState, chat: true }));

    if (link) {
      setValue((prev: string) => {
        let text = prev;
        if (prev.trimEnd().endsWith(link)) {
          const index = prev.lastIndexOf(link);
          text = prev.slice(0, index).trimEnd();
        } else if (text.trimEnd()) {
          text = `${text.trimEnd()} ${link}`;
        } else {
          text = link;
        }
        return text;
      });

      MixpanelWrapperInstance.track(EventNames.MemberCallChatLink, {
        targetLabel: 'Append video link (chat)',
        targetType: ClickableElement.BUTTON,
        memberUuid: patient.uuid,
        memberName: `${patient.fullname}`,
        providerUuid: userState.uuid,
        source: 'append-video-link-chat',
        videoVersion: 'v2',
      });
      return;
    }

    Toast.show('error', "Couldn't append the connection link to the chat");
  };

  const sendMeetingConnectionLinkViaSms = async (onClose: () => void): Promise<void> => {
    setMeetingConnectionLinkToolbarStatuses((prevState) => ({ ...prevState, sms: false }));

    const response = await appointmentsClient.sendMeetingConnectionLink(
      {
        memberUuid: patient.uuid,
        providerUuid: userState.uuid!,
      },
      [NotificationMethod.Sms]
    );

    const smsSent = response ? !!response[patient.uuid].sms : false;
    setMeetingConnectionLinkToolbarStatuses((prevState) => ({ ...prevState, sms: true }));

    if (smsSent) {
      MixpanelWrapperInstance.track(EventNames.MemberProfileSendAppointmentReminderViaSms, {
        targetLabel: 'Send Video link by SMS',
        targetType: ClickableElement.BUTTON,
        targetLocation: 'Member chat',
        videoVersion: 'v2',
      });

      Toast.show('success', 'The connection link was sent via sms');
    } else {
      Toast.show('error', "Couldn't send the connection link via sms");
    }
    onClose();
  };

  const onNo = async (onClose: () => void): Promise<void> => {
    onClose();
  };

  const displayModalToSendMeetingConnectionLinkViaSms = (): void => {
    confirmationDispatch({
      type: ConfirmationActionTypes.SHOW,
      payload: {
        onYes: sendMeetingConnectionLinkViaSms,
        onNo,
        message: `Are you sure you want to send the video link by SMS to ${patient.first_name} ${patient.last_name}?`,
      },
    });
  };

  const actions: Action[] = [
    {
      id: 'task',
      display: 'task',
    },
    {
      id: 'stick',
      display: 'stick',
    },
  ];

  useEffect(() => {
    const data = providerState.data.map((p: Provider) => ({
      id: p.id,
      display: `${p.first_name} ${p.last_name}`,
    }));

    setProviders(data);
  }, [providerState.data]);

  useEffect(() => {
    // Set direct message
    if (!!patient?.chat?.message) {
      setValue(patient.chat.message);
    }
  }, [patient?.chat?.message]);

  return (
    <>
      <IconContainer>
        {type === 'direct' && !(isCurProviderHidden === true) ? (
          <>
            <div
              data-tip='Append/Remove Video link'
              data-for='append-remove-video-link'
              key='append-remove-video-link'>
              <ReactTooltip id='append-remove-video-link' place='top' type='dark' effect='float' />
              <img
                className={
                  meetingConnectionLinkToolbarStatuses.chat
                    ? styles.videoCallButtonEnabled
                    : styles.videoCallButtonDisabled
                }
                src={videoLinkChat}
                height='30'
                width='30'
                alt=''
                onClick={appendMeetingConnectionLinkToChat}
              />
            </div>

            <div
              data-tip='Send Video link by SMS'
              data-for='send-video-link-sms'
              key='send-video-link-sms'>
              <ReactTooltip id='send-video-link-sms' place='top' type='dark' effect='float' />
              <img
                className={
                  meetingConnectionLinkToolbarStatuses.sms
                    ? styles.videoCallButtonEnabled
                    : styles.videoCallButtonDisabled
                }
                src={videoLinkSMS}
                height='30'
                width='30'
                alt=''
                onClick={displayModalToSendMeetingConnectionLinkViaSms}
              />
            </div>

            <div
              data-tip='Send email video link'
              data-for='email-reminder-video-link'
              key='email-reminder-video-link'>
              <ReactTooltip id='email-reminder-video-link' place='top' type='dark' effect='float' />
              <img
                className={
                  meetingConnectionLinkToolbarStatuses.email
                    ? styles.videoCallButtonEnabled
                    : styles.videoCallButtonDisabled
                }
                src={email}
                height='30'
                width='30'
                alt=''
                onClick={sendMeetingConnectionLinkViaEmail}
              />
            </div>
          </>
        ) : null}
      </IconContainer>
      <InputMessageBox type={type}>
        {type === 'team' ? (
          <MentionsInput
            autoFocus={autoFocus}
            value={value}
            onChange={handleOnChange}
            style={inputStyle}
            placeholder='Team Chat'>
            <Mention
              markup='@[[__display__]]((id:__id__))'
              trigger='@'
              data={providers}
              style={mentionStyle}
              displayTransform={(_id: string, display: string) => `@${display}`}
              appendSpaceOnAdd
            />

            <Mention
              markup='#[[__display__]]'
              trigger='#'
              data={actions}
              style={actionStyle}
              displayTransform={(_id: string, display: string) => `#${display}`}
              appendSpaceOnAdd
            />
          </MentionsInput>
        ) : (
          <MentionsInput
            autoFocus={autoFocus}
            value={value}
            onChange={handleOnChange}
            style={inputStyle}
            disabled={type === 'direct' && isCurProviderHidden}
            placeholder='Patient Chat'>
            <Mention
              markup='@[[__display__]]((id:__id__))'
              trigger='avoidtriggeringhere'
              data={[]}
              style={mentionStyle}
              appendSpaceOnAdd
            />
          </MentionsInput>
        )}
        <TrackableElement
          customEventName='Sent a message'
          name='Send Message'
          type={ClickElementTypes.BUTTON}
          source={type === 'team' ? 'Team' : 'Patient'}
          eventHandlerName={EventHandlerNames.ONCLICK}
          inputData={{
            isOriginalUser: userState.isOriginalUser,
            originalUser: getOriginalUserFullName(),
          }}>
          <SendButton
            className='send'
            disabled={(type === 'direct' && isCurProviderHidden) || !value.trim()}
            onClick={handleOnClick}>
            <Icon icon='send' size={20} color='white' />
          </SendButton>
        </TrackableElement>
      </InputMessageBox>
    </>
  );
};

export default MessageInput;
